import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useSubmitBatchSearchMutation } from '@/store/api/BatchSearch/BatchSearch.query'
import type { BatchSearchRequest } from '@/store/api/BatchSearch/BatchSearch.types'
import {
  selectUserInfo,
  selectSelectedWorkStation,
  selectSelectedTeam,
} from '@/store/api/Users/Users.selector'
import type {
  BatchRow,
  BatchValidationResult,
} from '@/utils/batchFileValidation'
import { parseAndValidateBatchFile } from '@/utils/batchFileValidation'

import { BatchSearchSummaryStep } from './BatchSearchSummaryStep'
import { BatchSearchUploadStep } from './BatchSearchUploadStep'
import { BatchSearchValidationStep } from './BatchSearchValidationStep'
import MobileFeedbackScreen from './components/MobileFeedbackScreen'

export const BatchSearch = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const userInfo = useSelector(selectUserInfo)
  const selectedWorkstation = useSelector(selectSelectedWorkStation)
  const selectedTeam = useSelector(selectSelectedTeam)

  const [submitBatchSearch] = useSubmitBatchSearchMutation()

  const [step, setStep] = useState(1)
  const [validationResult, setValidationResult] =
    useState<BatchValidationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [editedData, setEditedData] = useState<Record<string, string>>({})
  const [dynamicValidationStatus, setDynamicValidationStatus] = useState<{
    hasErrors: boolean
    totalErrors: number
    validRows: number
    validRowIndexes?: number[]
  } | null>(null)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      const screenWidth = window.innerWidth
      const isSmallScreen = screenWidth < 768

      setIsMobile(isSmallScreen)
    }

    checkIsMobile()

    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  const handleFileUpload = useCallback(async (file: File) => {
    setValidationResult(null)
    setEditedData({})
    setLoading(true)
    setUploadedFile(file)

    try {
      const result = await parseAndValidateBatchFile(file, {
        requireConsentCheck: (row) =>
          row.useCase && row.useCase.toLowerCase().includes('consent'),
      })

      if (result.validRows.length === 0) {
        const errorMessage = t(
          'dashboard.batchSearch.noValidSubjects',
          'Cannot upload batch file with 0 valid subjects. Please check your file and try again.',
        )
        toast.error(errorMessage, { duration: 10000 })
        setLoading(false)
        return
      }

      setValidationResult(result)
      setStep(2)
      setLoading(false)
    } catch (err: any) {
      toast.error(err?.message || 'Unknown error', { duration: 10000 })
      setLoading(false)
    }
  }, [])

  const handleBack = () => {
    if (step === 3) {
      setStep(2)
    } else {
      setStep(step - 1)
      setValidationResult(null)
      setUploadedFile(null)
      setEditedData({})
    }
  }

  const handleCancel = () => {
    setStep(1)
    setValidationResult(null)
    setUploadedFile(null)
    setEditedData({})
    setDynamicValidationStatus(null)
    setIsTermsAccepted(false)
  }

  const handleContinueOrSubmit = () => {
    if (step === 3) {
      if (validationResult) {
        const processedSubjects = validationResult.detailedResults
          .filter((result) => {
            // Use dynamic validation status if available and edits were made
            if (
              dynamicValidationStatus?.validRowIndexes &&
              Object.keys(editedData).length > 0
            ) {
              return dynamicValidationStatus.validRowIndexes.includes(
                result.rowIndex,
              )
            }
            // Otherwise use original validation
            return result.isValid
          })
          .map((result) => {
            const processedData: any = { ...result.data }

            Object.keys(editedData).forEach((key) => {
              const dashIndex = key.indexOf('-')
              if (dashIndex === -1) return

              const rowIndex = key.substring(0, dashIndex)
              const field = key.substring(dashIndex + 1)

              if (parseInt(rowIndex, 10) === result.rowIndex) {
                processedData[field] = editedData[key]
              }
            })

            return processedData
          })

        handleBatchSubmit(processedSubjects as BatchRow[])
      }
    } else {
      setStep(step + 1)
    }
  }

  const handleDataChange = useCallback(
    (newEditedData: Record<string, string>) => {
      setEditedData(newEditedData)
    },
    [],
  )

  const handleValidationStatusChange = useCallback(
    (
      hasErrors: boolean,
      totalErrors: number,
      validRows: number,
      validRowIndexes?: number[],
    ) => {
      setDynamicValidationStatus({
        hasErrors,
        totalErrors,
        validRows,
        validRowIndexes,
      })
    },
    [],
  )

  const handleBatchSubmit = useCallback(
    async (finalData: BatchRow[]) => {
      setIsSubmitting(true)
      try {
        const batchRequest: BatchSearchRequest = {
          metadata: {
            submittedAt: new Date().toISOString(),
            submittedBy: {
              id: userInfo.userId || '',
              name: `${userInfo.firstname} ${userInfo.lastname}`,
              email: userInfo.email || '',
            },
            workspace: {
              id: selectedWorkstation?._id || '',
              name: selectedWorkstation?.name || '',
            },
            team: {
              id: selectedTeam?._id || '',
              name: selectedTeam?.name || '',
            },
            totalRows: validationResult?.detailedResults.length || 0,
            validRows: finalData.length,
          },
          subjects: finalData as any[],
        }

        console.log('Submitting batch data:', batchRequest)

        const response = await submitBatchSearch(batchRequest).unwrap()
        navigate(AppRoutes.batchSearchInProgress, {
          state: {
            batchInfo: {
              batchId: response.batchId,
              processedSubjects: response.processedSubjects,
            },
          },
        })

        setStep(1)
        setValidationResult(null)
        setUploadedFile(null)
        setEditedData({})
        setDynamicValidationStatus(null)
        setIsTermsAccepted(false)
      } catch (err: any) {
        console.error('Batch search submission error:', err)

        let errorMessage: string
        if (err?.status === 404) {
          errorMessage = t(
            'dashboard.batchSearch.endpointNotFound',
            'Batch search endpoint not available. Please contact support.',
          )
        } else if (err?.data?.message) {
          errorMessage = err.data.message
        } else if (err?.message) {
          errorMessage = err.message
        } else {
          errorMessage = t(
            'dashboard.batchSearch.submissionError',
            'Failed to submit batch search. Please try again.',
          )
        }

        toast.error(errorMessage, { duration: 10000 })
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      t,
      userInfo,
      selectedWorkstation,
      selectedTeam,
      validationResult,
      submitBatchSearch,
      navigate,
    ],
  )

  const isDisabledNextButton = () => {
    if (step === 2 && validationResult) {
      if (dynamicValidationStatus) {
        return (
          dynamicValidationStatus.hasErrors ||
          dynamicValidationStatus.validRows === 0
        )
      }
      return (
        validationResult.errors.length > 0 ||
        validationResult.validRows.length === 0
      )
    }
    if (step === 3) {
      return !isTermsAccepted || isSubmitting
    }
    return false
  }

  if (isMobile) {
    return (
      <WeScreen useHeader={false} title={t('dashboard.sidebar.batchSearch')}>
        <MobileFeedbackScreen />
      </WeScreen>
    )
  }

  return (
    <WeScreen
      useHeader={step !== 1}
      onClickBack={handleBack}
      useNextButton={step === 2 || step === 3}
      onClickNextButton={
        step === 2 || step === 3 ? handleContinueOrSubmit : undefined
      }
      isDisabledNextButton={isDisabledNextButton()}
      rightSideHeaderContent={
        step === 2 || step === 3 ? (
          <Button variant="secondary" onClick={handleCancel}>
            {t('dashboard.newSearch.legalDisclaimer.cancel', 'Cancel')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col h-full w-full">
        <div className="flex-1 flex justify-center items-center p-6">
          {step === 1 && (
            <BatchSearchUploadStep
              onFileUpload={handleFileUpload}
              loading={loading}
              uploadedFile={uploadedFile}
            />
          )}
          {step === 2 && validationResult && (
            <BatchSearchValidationStep
              validationResult={validationResult}
              onDataChange={handleDataChange}
              onValidationStatusChange={handleValidationStatusChange}
            />
          )}
          {step === 3 && validationResult && (
            <BatchSearchSummaryStep
              validationResult={validationResult}
              editedData={editedData}
              isTermsAccepted={isTermsAccepted}
              onTermsChange={setIsTermsAccepted}
              dynamicValidationStatus={dynamicValidationStatus}
            />
          )}
        </div>
      </div>
    </WeScreen>
  )
}
